import { schema } from '@/features/settings/components/NotificationConfiguration/form/schema'
import {
    Form,
    OnFormSubmit,
} from '@/features/settings/components/NotificationConfiguration/form/types'
import { getDefaultValues } from '@/features/settings/components/NotificationConfiguration/form/utils/getDefaultValues'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useRef } from 'react'
import {
    FieldPath,
    UseFormReturn,
    useController,
    useFormContext,
    useForm as useRHFForm,
    useWatch,
} from 'react-hook-form'

export const useForm = (initialValues: Form): UseFormReturn<Form, never, Form> => {
    return useRHFForm<Form, never, Form>({
        defaultValues: getDefaultValues(initialValues),
        resolver: zodResolver<Form, never, Form>(schema),
        mode: 'onChange',
        shouldUnregister: false,
    })
}

export const useEnabledField = <K extends keyof Form>(key: K) => {
    const {
        field: { value, onChange },
    } = useController<Form, FieldPath<Form>>({ name: `${key}.enabled` as FieldPath<Form> })
    return [value as boolean, onChange as (v: boolean) => void] as const
}

export const useSoundField = <K extends keyof Form>(key: K) => {
    const {
        field: { value, onChange },
    } = useController<Form, FieldPath<Form>>({ name: `${key}.sound` as FieldPath<Form> })
    return [Boolean(value), onChange as (v: boolean) => void] as const
}

export const useWatchFormValues = (): Form => {
    return useWatch<Form>({}) as Form
}

export const useFormSubmit = (
    onFormSubmit: OnFormSubmit
): [{ isValid: boolean; isSubmitting: boolean; isDirty: boolean }, () => void] => {
    const {
        handleSubmit,
        formState: { isSubmitting, isValid, isDirty },
    } = useFormContext<Form, any, Form>()

    const handleValid = async (data: Form) => {
        await onFormSubmit(data)
    }

    const submitHandler = handleSubmit(handleValid)

    return [{ isSubmitting, isValid, isDirty }, submitHandler]
}

export const useFormAutosave = (onFormSubmit: OnFormSubmit) => {
    const watchedValues = useWatchFormValues()
    const [state, save] = useFormSubmit(onFormSubmit)
    const lastSaved = useRef<Form | null>(null)

    useEffect(() => {
        if (state.isSubmitting || !state.isDirty) return

        if (
            lastSaved.current &&
            JSON.stringify(lastSaved.current) === JSON.stringify(watchedValues)
        ) {
            return
        }

        lastSaved.current = watchedValues
        save()
    }, [watchedValues, state.isDirty, state.isSubmitting, save])

    return state
}
